import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { DialogBaseComponent } from '../../common/dialog/dialog-base/dialog-base.component';
import { DialogService } from 'src/app/services/UI/dialog.service';
import { Category } from 'src/app/common/models/support/Category';
import { SearchApiService } from 'src/app/services/API/support/search-api.service';
import { FileUtil } from 'src/app/services/API/support/file-util';
import { TranslateService } from '@ngx-translate/core';
import { SupportService } from 'src/app/services/API/support/support.service';
import { GuidGenerator } from 'src/app/common/helpers/guid-generator';
import { LoadingService } from 'src/app/services/UI/loading.service';
import { timeout } from 'rxjs/operators';

@Component({
    selector: 'app-support-page',
    templateUrl: './support-page.component.html',
    styleUrls: ['./support-page.component.scss'],
    standalone: false
})
export class SupportPageComponent extends DialogBaseComponent implements OnInit {

  supportForm: UntypedFormGroup;
  selectedFiles: File[] = [];
  public data: any;
  readonly formConfig = environment.CONTACT_FORM;
  readonly allowedFileTypes: string[] = this.formConfig.FILE_ATTACHMENTS.FILE_TYPES;
  readonly maxFileSize: number = this.formConfig.FILE_ATTACHMENTS.MAX_SIZE;
  readonly maxFileCount: number = this.formConfig.FILE_ATTACHMENTS.MAX_COUNT;
  readonly subjectMaxLength: number = this.formConfig.SUBJECT_MAX_LENGTH;
  readonly messageMaxLength: number = this.formConfig.MESSAGE_MAX_LENGTH;
  allowedFileTypesString: string = this.allowedFileTypes.join(', ');
  maxFileSizeString: string = `${this.maxFileSize / 1024 / 1024}MB`;

  fileErrorKey: string = '';
  isSystemfailer: boolean = false;
  isFileLimitReached: boolean = false;
  categories: Category[] = [];
  isSubmitted: boolean = false;
  isLoadingVisible: boolean = false;
  titleText = 'more_help.lbl_title';
  isOpen = false;
  responseMessage = '';

  constructor(
    private supportService: SupportService,
    private fileUtil: FileUtil,
    private dialogService: DialogService,
    private ref1: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private searchApiService: SearchApiService,
    private translateService: TranslateService,
    private loadingService: LoadingService,
  ) {
    super(dialogService, ref1);
    this.supportForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email, Validators.pattern(this.formConfig.EMAIL_PATTERN)]],
      subject: ['', [Validators.required, Validators.maxLength(this.subjectMaxLength)]],
      category: ['', Validators.required],
      message: ['', [Validators.required, Validators.maxLength(this.messageMaxLength)]],
      attachment: [null]
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.isOpen = true;
    }, 30);
    this.loadUserData();
    this.loadCategories().then(() => this.isLoadingVisible = false);
  }

  onFileSelected(event: any) {
    if (event.target.files) {
      this.selectedFiles = Array.from(event.target.files);
    }
  }

  onSubmit(): void {
    if (this.supportForm.invalid) {
      return;
    }
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.isLoadingVisible = true;
    const formData = new FormData();
    formData.append('name', this.supportForm.get('name')?.value);
    formData.append('email', this.supportForm.get('email')?.value);
    formData.append('subject', this.supportForm.get('subject')?.value);
    formData.append('category', this.supportForm.get('category')?.value);
    formData.append('message', this.supportForm.get('message')?.value);

    this.selectedFiles.forEach(file => {
      formData.append(`attachments`, file);
    });

    this.supportService.postFormData(formData).subscribe({
      next: (response) => {
        this.loadingService.hide(processId);
        this.isSubmitted = true;
        this.responseMessage = this.translateService.instant("more_help.lbl_contact_success").replace(/\n/g, '<br>');
        this.supportForm.reset();
        this.selectedFiles = [];
      },
      error: (err) => {
        this.loadingService.hide(processId);
        this.isSubmitted = true;
        this.responseMessage = this.translateService.instant("more_help.contact_form.errors.lbl_system_fail");
        this.isSystemfailer = true;
      }
    });


  }

  private loadUserData(): void {
    let userName: string = '';
    let email: string = '';

    userName = this.data.name;
    email = this.data.email ?? '';
    this.supportForm.patchValue({
      name: userName,
      email: email
    });
  }

  private loadCategories(): Promise<void> {
  let processId = GuidGenerator.generate();
  this.loadingService.show(processId);

  return new Promise((resolve, reject) => {
    this.searchApiService.getCategories()
      .pipe(timeout(30000)) 
      .subscribe({
        next: (categories: Category[]) => {
          this.categories = categories;
          this.addAdditionalCategories(this.categories);
          resolve();
          this.loadingService.hide(processId);
        },
        error: (err) => {
          reject(err);
          this.loadingService.hide(processId);
        }
      });
  });
}

  private addAdditionalCategories(categories: Category[]): void {
    let additionalCategories = environment.CONTACT_FORM.ADDITIONAL_CATEGORIES;
    additionalCategories.forEach((categoryName) => {
      let category = new Category();
      category.name = this.translateService.instant("more_help.contact_form.additional_categories.lbl_" + categoryName);
      categories.push(category);
    });
  }

  public onFileChange(event: any): void {
    const files = event.target.files;
    this.fileErrorKey = '';

    // Check max file count
    if (this.selectedFiles.length + files.length > this.maxFileCount) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileType = this.getFileExtension(file.name).toLowerCase();

      // Check file type
      if (!this.allowedFileTypes.includes(fileType)) {
        this.fileErrorKey = "more_help.contact_form.errors.lbl_" + 'file_type';
        continue;
      }

      // Check file size
      if (file.size > this.maxFileSize) {
        this.fileErrorKey = "more_help.contact_form.errors.lbl_" + 'file_size';
        continue;
      }

      this.selectedFiles.push(file);
    }

    if (this.selectedFiles.length === this.maxFileCount) {
      this.isFileLimitReached = true;
    }

    // Update form with valid files
    this.supportForm.patchValue({
      attachment: this.selectedFiles.length > 0 ? this.selectedFiles : null
    });
  }

  public removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);

    this.supportForm.patchValue({
      attachment: this.selectedFiles.length > 0 ? this.selectedFiles : null
    });
    this.isFileLimitReached = false;
    const fileInput = document.getElementById('attachment') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  public getFileExtension(fileName: string): string {
    return this.fileUtil.getFileExtension(fileName);
  }

  public getFileNameWithoutExtention(fileName: string): string {
    return this.fileUtil.getFileNameWithoutExtention(fileName);
  }

  public isValid(controlName: string): boolean {
    return (this.supportForm.get(controlName)?.invalid && this.supportForm.get(controlName)?.touched) ?? false;
  }

  closePopup() {
    this.isOpen = false
    this.dialogService.close(null);
  }


}
